import { getChangedRanges, NodeWithPos } from '@tiptap/core'
import { isChangeOrigin } from '@tiptap/extension-collaboration'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { EditorState, Plugin, PluginKey, Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { MINUTE_NODE_INTERNAL_TYPE } from '../../../../../../backend/src/tiptap/editor/extensions/shared/minute-internal-types'

/**
 * Locks the agenda items a targeted minutes regeneration is rewriting.
 *
 * The content of a locked item is about to be replaced wholesale by the server, so it is shown as a shimmer and
 * kept out of reach of the caret. Everything else in the document stays editable, which is the whole point of
 * regenerating part of the minutes rather than all of them.
 */

const LOCKED_ITEM_CLASS = 'minute-node--regenerating'

export type LockedItemsPluginOptions = {
  getLockedTemplateItemIds: () => number[]
}

export type LockedRange = {
  from: number
  to: number
}

type LockedItemsPluginState = {
  ranges: LockedRange[]
  decorations: DecorationSet
  /** The lock set the ranges were built from, so a change to it is cheap to notice. */
  lockedKey: string
}

export const lockedItemsPluginKey = new PluginKey<LockedItemsPluginState>('lockedItems')

function buildLockedKey(lockedTemplateItemIds: number[]): string {
  return [...lockedTemplateItemIds].sort((first, second) => first - second).join(',')
}

/**
 * Whether two lock sets name the same agenda items, so that a meeting refetch handing back an equal-but-new array
 * is not mistaken for a regeneration starting or ending. Order is not part of the answer.
 */
export function haveSameLockedItems(first: number[], second: number[]): boolean {
  return buildLockedKey(first) === buildLockedKey(second)
}

/**
 * Pairs each child of a node with its document position. ProseMirror hands positions to a void callback, so this
 * is where that is turned into something the collectors below can map over.
 */
export function placeChildren(parent: ProseMirrorNode, contentStart: number): NodeWithPos[] {
  const placed: NodeWithPos[] = []
  parent.forEach((node, offset) => placed.push({ node, pos: contentStart + offset }))
  return placed
}

function isLockedMinuteNode(node: ProseMirrorNode, lockedTemplateItemIds: Set<string>): boolean {
  const templateItemId = node.attrs['templateItemId']
  return node.attrs['internalType'] === MINUTE_NODE_INTERNAL_TYPE && typeof templateItemId === 'string' && lockedTemplateItemIds.has(templateItemId)
}

function collectLockedRanges(parent: ProseMirrorNode, contentStart: number, lockedTemplateItemIds: Set<string>): LockedRange[] {
  // Text nodes are most of a document and this reruns on every keystroke, so they are turned away before
  // anything is allocated for them.
  if (parent.childCount === 0) {
    return []
  }

  return placeChildren(parent, contentStart).flatMap(({ node, pos }) =>
    isLockedMinuteNode(node, lockedTemplateItemIds) ? [{ from: pos, to: pos + node.nodeSize }] : collectLockedRanges(node, pos + 1, lockedTemplateItemIds)
  )
}

/**
 * The document ranges covered by the locked agenda items. Subitems are inside their parent's range already, so
 * a locked item is never descended into.
 */
export function findLockedRanges(doc: ProseMirrorNode, lockedTemplateItemIds: number[]): LockedRange[] {
  if (lockedTemplateItemIds.length === 0) {
    return []
  }

  return collectLockedRanges(doc, 0, new Set(lockedTemplateItemIds.map((templateItemId) => templateItemId.toString())))
}

/** Whether a transaction would change anything inside a locked range, measured against the document it starts from. */
export function touchesLockedRanges(transaction: Transaction, ranges: LockedRange[]): boolean {
  if (ranges.length === 0) {
    return false
  }

  return getChangedRanges(transaction).some(({ oldRange }) => ranges.some((range) => oldRange.from < range.to && oldRange.to > range.from))
}

export function overlapsLockedRange(state: EditorState, from: number, to: number): boolean {
  const pluginState = lockedItemsPluginKey.getState(state) ?? null
  return pluginState !== null && pluginState.ranges.some((range) => from < range.to && to > range.from)
}

/**
 * Whether any of the ranges an action would rewrite sits inside a locked agenda item.
 *
 * An action that spans several ranges — resolving a suggestion covering an insertion and a deletion, say — has
 * its whole transaction rejected if one of them is locked, so it must be turned away as a whole.
 */
export function coversLockedRange(state: EditorState, ranges: LockedRange[]): boolean {
  return ranges.some((range) => overlapsLockedRange(state, range.from, range.to))
}

export function createLockedItemsPlugin(options: LockedItemsPluginOptions): Plugin {
  function build(doc: ProseMirrorNode): LockedItemsPluginState {
    const lockedTemplateItemIds = options.getLockedTemplateItemIds()
    const ranges = findLockedRanges(doc, lockedTemplateItemIds)
    return {
      ranges,
      decorations: DecorationSet.create(
        doc,
        ranges.map((range) => Decoration.node(range.from, range.to, { class: LOCKED_ITEM_CLASS }))
      ),
      lockedKey: buildLockedKey(lockedTemplateItemIds),
    }
  }

  return new Plugin<LockedItemsPluginState>({
    key: lockedItemsPluginKey,

    state: {
      init(_, state) {
        return build(state.doc)
      },
      apply(transaction, pluginState, _oldState, newState) {
        const lockedKey = buildLockedKey(options.getLockedTemplateItemIds())
        if (transaction.docChanged || lockedKey !== pluginState.lockedKey) {
          return build(newState.doc)
        }
        return pluginState
      },
    },

    // Locking is a display concern, so it must never reject what the server or another collaborator sends —
    // the regenerated content arrives through exactly those transactions.
    filterTransaction(transaction, state) {
      if (isChangeOrigin(transaction) || transaction.steps.length === 0) {
        return true
      }
      const pluginState = lockedItemsPluginKey.getState(state) ?? null
      return pluginState === null || !touchesLockedRanges(transaction, pluginState.ranges)
    },

    props: {
      decorations(state) {
        return lockedItemsPluginKey.getState(state)?.decorations ?? DecorationSet.empty
      },
    },
  })
}
